import { NgTemplateOutlet } from '@angular/common';
import { Component, computed, DestroyRef, effect, input, InputSignal, OnInit, Signal, signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatTabsModule } from '@angular/material/tabs';
import { TranslateModule } from '@ngx-translate/core';
import { forkJoin } from 'rxjs';
import * as uuid from 'uuid';
import { AllowedExtensions, IChatAttachment, IChatAttachments, IChatFileAttachment, IChatMessage, IChatMessages, IChatReactionSocketPayload, IChatReadMessage, IChatReadUser, IMessageLocalData, INewChatMessage, PinnedIndicator } from '../../models/chat';
import { RequestParams } from '../../models/requestParams';
import { ChatService, IMAGE_EXTENSIONS, VIDEO_EXTENSIONS } from '../../services/chat.service';
import { SessionService } from '../../services/session.service';
import { SnackBarService } from '../../services/snack-bar.service';
import { SocketService } from '../../services/socket.service';
import { Utils } from '../../services/utils.service';
import { PhChatAttachmentsComponent } from '../ph-chat-attachments/ph-chat-attachments.component';
import { PhChatInputComponent } from '../ph-chat-input/ph-chat-input.component';
import { PhChatMessagesComponent } from '../ph-chat-messages/ph-chat-messages.component';
import { PhLoaderComponent } from '../ph-loader/ph-loader.component';
import { TableService } from '../../services/table.service';
import { PhChatPinnedBarComponent } from '../ph-chat-pinned-bar/ph-chat-pinned-bar.component';

const EMPTY_RESPONSE = {
    results: [],
    count: 0,
    next: null,
    previous: null
};

@Component({
    selector: 'ph-chat',
    templateUrl: './ph-chat.component.html',
    styleUrls: ['./ph-chat.component.scss'],
    imports: [
        MatTabsModule,
        PhChatMessagesComponent,
        PhChatInputComponent,
        PhLoaderComponent,
        NgTemplateOutlet,
        TranslateModule,
        PhChatAttachmentsComponent,
        PhChatPinnedBarComponent
    ]
})
export class PhChatComponent implements OnInit {

    chatName: InputSignal<string> = input('New chat');
    emptySubtitle: InputSignal<string> = input('CHAT.WRITE_FOR_USE');

    private _requestParams: RequestParams;
    mediaAttachments: WritableSignal<IChatAttachments> = signal<IChatAttachments>(null);
    documentsAttachments: WritableSignal<IChatAttachments> = signal<IChatAttachments>(null);
    loading: WritableSignal<boolean> = signal<boolean>(true);
    loadingAttachmentsPage: WritableSignal<boolean> = signal<boolean>(false);
    messages: WritableSignal<IChatMessages> = signal<IChatMessages>(EMPTY_RESPONSE);
    selectedTabIndex: WritableSignal<number> = signal<number>(0);
    private attachmentsRequestSeq: number = 0;
    private lastLoadedAttachmentFilters: Record<'media' | 'documents', string | null> = {
        media: null,
        documents: null
    };

    pinnedMessages: Signal<Array<IChatMessage>> = computed(() => {
        return this.chatService.pinnedMessages() ?? [];
    });

    currentPinnedIndex: WritableSignal<number> = signal<number>(-1);

    displayPinnedMessage: Signal<Array<IChatMessage>> = computed((): Array<IChatMessage> => {
        const messages: Array<IChatMessage> | null = this.pinnedMessages();
        if (!messages?.length) return [];
        const myUserId: number = this.sessionService.defaults.user_id;
        const pendingRestoreIds: Set<number> = this.chatService.pendingRestoreMessageIds();

        return [...messages]
            .filter((m: IChatMessage): boolean => {
                if (!m.deleted) return true;
                if (typeof m.id !== 'number') return false;
                const isOwnDeleted: boolean = m.user?.id === myUserId;
                return isOwnDeleted && pendingRestoreIds.has(m.id);
            })
            .sort((a: IChatMessage, b: IChatMessage): number => {
                return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
            });
    });

    currentPinnedMessage: Signal<IChatMessage | null> = computed((): IChatMessage | null => {
        const list: Array<IChatMessage> = this.displayPinnedMessage();
        if (!list.length) return null;

        const index: number = this.currentPinnedIndex();
        const safeIndex: number = Math.max(0, Math.min(index, list.length - 1));
        return list[safeIndex];
    });

    pinnedIndicators: Signal<Array<PinnedIndicator>> = computed((): Array<PinnedIndicator> => {
        const list: Array<IChatMessage> = this.displayPinnedMessage();
        const total: number = list.length;
        const count: number = total > 3 ? 3 : total;
        if (!count) return [];

        const current: number = this.currentPinnedIndex();
        let activeIndex: number;

        if (total > 3) {
            if (current === 0) {
                activeIndex = 0;
            } else if (current === total - 1) {
                activeIndex = count - 1;
            } else {
                activeIndex = 1;
            }
        } else {
            activeIndex = current;
        }

        return Array.from({ length: count }, (_: unknown, i: number): PinnedIndicator => {
            return { index: i, active: activeIndex === i };
        });
    });

    pinnedMessagesAsIChatMessages: ReturnType<typeof computed<IChatMessages>> = computed(() => {
        const pinned = this.pinnedMessages();
        return {
            results: [...pinned],
            count: pinned.length,
            next: null,
            previous: null
        };
    });

    loadingMoreMedia: WritableSignal<boolean> = signal<boolean>(false);
    loadingMoreDocuments: WritableSignal<boolean> = signal<boolean>(false);

    constructor(
        private readonly chatService: ChatService,
        private readonly destroyRef: DestroyRef,
        private readonly snackBarService: SnackBarService,
        private readonly sessionService: SessionService,
        private readonly socketService: SocketService,
        private readonly tableService: TableService
    ) {
        this._requestParams = new RequestParams({ page_size: 30 });

        effect((): void => {
            const messages: Array<IChatMessage> = this.displayPinnedMessage();
            const length: number = messages.length;
            if (length) {
                this.currentPinnedIndex.set(length - 1);
            } else {
                this.currentPinnedIndex.set(-1);
            }
        });

        effect((): void => {
            const tab: number = this.selectedTabIndex();
            const searchOpen: boolean = this.chatService.searchPanelOpen();
            const query: string = this.chatService.searchPanelQuery().trim();
            const loadingAttachments: boolean = this.loadingAttachmentsPage();
            const mediaQuery: string = this.chatService.mediaSearchFilter().trim();
            const documentsQuery: string = this.chatService.documentsSearchFilter().trim();
            const mediaCount: number | null = this.mediaAttachments()?.count ?? null;
            const documentsCount: number | null = this.documentsAttachments()?.count ?? null;

            if (!searchOpen || !query.length) {
                this.chatService.searchPanelResultsVisible.set(false);
                return;
            }

            if (tab === 2) {
                const visible: boolean = !loadingAttachments && mediaQuery.length > 0 && mediaCount != null && mediaCount === 0;
                this.chatService.searchPanelResultsVisible.set(visible);
                return;
            }

            if (tab === 3) {
                const visible: boolean = !loadingAttachments && documentsQuery.length > 0 && documentsCount != null && documentsCount === 0;
                this.chatService.searchPanelResultsVisible.set(visible);
                return;
            }

            this.chatService.searchPanelResultsVisible.set(true);
        });
    }

    ngOnInit(): void {
        this.chatService.activeChatTab.set(this.selectedTabIndex());
        this.chatService.searchPanelOpen.set(false);
        this.chatService.searchPanelQuery.set('');
        this.chatService.mediaSearchFilter.set('');
        this.chatService.documentsSearchFilter.set('');
        this.chatService.searchPanelResultsVisible.set(false);

        this.chatService.deleteAttachment$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((attachment: IChatAttachment): void => {
                this.chatService
                    .deleteAttachment(this.chatService.id, attachment.id)
                    .pipe(takeUntilDestroyed(this.destroyRef))
                    .subscribe({
                        next: (): void => this.loadAttachments(),
                        error: (err: any): void => this.snackHttpError(err)
                    });
            });

        this.chatService.reloadAttachments$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((): void => this.loadAttachments());

        this.loading.set(true);
        if (!Utils.isNullOrUndefined(this.chatService.focusMessageId())) {
            this.chatService.highlightAnchor.set(true);
            this.loadAroundMessage(this.chatService.focusMessageId());
        } else {
            this.loadLastReadMessages();
        }

        this.chatService.loadLastMessages$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.loadLastMessages());

        this.chatService.replyMessage$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.setActiveTab(0));

        this.chatService.editMessage$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((message: IChatMessage | null): void => {
                const fromPinnedTab: boolean = this.selectedTabIndex() === 1;
                this.setActiveTab(0);
                if (fromPinnedTab && typeof message?.id === 'number') {
                    this.chatService.loadAroundMessage$.next(message.id);
                }
            });

        this.chatService.newMessage$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((message: INewChatMessage): void => {
                const id: string = String(uuid.v4());
                const localData: IMessageLocalData = message.localData;
                delete message.localData;
                
                this.chatService.sendMessage(this.chatService.id, message)
                    .subscribe({
                        next: (savedMessage: IChatMessage): void => {
                            const saveReadSocket: IChatReadMessage | undefined = this.socketService.chatReadMessage.value;
                            if (saveReadSocket?.message_ids.includes(savedMessage.id as number)) {
                                const readUsersIds: Array<number> = savedMessage.read_by.map((user: IChatReadUser) => user.id);
                                if (!readUsersIds.includes(saveReadSocket.user.id)) {
                                    savedMessage.read_by.push(saveReadSocket.user);
                                }
                            }

                            this.messages.update((current: IChatMessages): IChatMessages => {
                                const results: Array<IChatMessage> = current.results.map((msg: IChatMessage) => {
                                    return msg.id === id ? savedMessage : msg;
                                });
                                return { ...current, results };
                            });

                            if (savedMessage.attachments?.length) {
                                this.mergeNewAttachmentsFromMessage(savedMessage);
                            }

                            if (this.messages().previous) this.chatService.scrollToBottom$.next();
                        },
                        error: (err: unknown): void => {
                            this.messages.update((current: IChatMessages): IChatMessages => {
                                const results: Array<IChatMessage> = current.results.map((msg: IChatMessage) =>
                                    msg.id === id ? { ...msg, status: 'failed' } : msg
                                );
                                return { ...current, results };
                            });
                            if (this.messages().previous) this.chatService.scrollToBottom$.next();

                            this.snackHttpError(err);
                        }
                    });
                
                if (this.messages().previous) return;
                const tempMessage: IChatMessage = {
                    user: {
                        id: this.sessionService.defaults.user_id,
                        email: this.sessionService.defaults.email,
                        photo: this.sessionService.defaults.image,
                        full_name: this.sessionService.defaults.fullName
                    },
                    content: message.content,
                    created_at: new Date().toISOString(),
                    updated_at: new Date().toISOString(),
                    attachments: message.attachments?.map((attachment: { key: string, name: string, file: string }) => ({
                        file: attachment.file,
                        key: attachment.key,
                        name: attachment.name,
                        content_type: attachment.name.split('.').pop() || ''
                    })) || [],
                    id: id
                };

                if (message.reply_to) {
                    tempMessage.reply_to = {
                        id: message.reply_to,
                        content: localData?.replyContent,
                        user: localData?.replyUser
                    };
                }

                this.messages.update((current: IChatMessages): IChatMessages => {
                    return {
                        ...current,
                        results: [...current.results, tempMessage]
                    };
                });
            });

        this.chatService.updateMessageRequest$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(({ message, payload }: { message: IChatMessage, payload: Record<string, unknown> }): void => {
                if (typeof message.id !== 'number') return;
                this.chatService.updateMessage(this.chatService.id, message.id, payload as Partial<IChatMessage>)
                    .pipe(takeUntilDestroyed(this.destroyRef))
                    .subscribe({
                        next: (savedMessage: IChatMessage): void => {
                            this.messages.update((current: IChatMessages): IChatMessages => {
                                const results = current.results.map((msg: IChatMessage) => {
                                    if (msg.id !== savedMessage.id) return msg;

                                    const merged: IChatMessage = { ...msg, ...savedMessage };

                                    if (!merged.status || merged.status === 'pending') {
                                        if (merged.read_by?.length) {
                                            merged.status = 'read';
                                        } else {
                                            merged.status = 'sent';
                                        }
                                    }

                                    return merged;
                                });
                                return { ...current, results };
                            });
                        },
                        error: (err: unknown): void => {
                            this.messages.update((current: IChatMessages): IChatMessages => ({
                                ...current,
                                results: current.results.map((msg: IChatMessage) =>
                                    msg.id === message.id ? { ...msg, status: 'failed' } : msg
                                )
                            }));
                            this.snackHttpError(err);
                        }
                    });
            });

        this.socketService.chatNewMessage
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((response: { message: IChatMessage }): void => {
                if (this.messages().previous) return;
                
                this.messages.update((current: IChatMessages): IChatMessages => {
                    const isDuplicate = current.results.some((msg: IChatMessage) => {
                        if (msg.id === response.message.id) return true;
                        
                        const isTemp: boolean = typeof msg.id === 'string' && msg.id.includes('-');
                        const sameUser: boolean = msg.user?.id === response.message.user?.id;
                        const sameContent: boolean = (msg.content ?? '').trim() === (response.message.content ?? '').trim();
                        
                        return isTemp && sameUser && sameContent;
                    });

                    if (isDuplicate) return current;

                    this.chatService.checkScrollBottomPosition$.next();

                    return {
                        ...current,
                        results: [...current.results, response.message]
                    };
                });

                if (response.message.attachments?.length) {
                    this.mergeNewAttachmentsFromMessage(response.message);
                }

                this.readMessages();
            });

        this.socketService.chatEditMessage
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((response: { message: IChatMessage }): void => {
                const editedMessage: IChatMessage = response?.message;
                if (!editedMessage?.id) return;

                this.messages.update((current: IChatMessages): IChatMessages => {
                    const results: Array<IChatMessage> = current.results.map((msg: IChatMessage): IChatMessage => {
                        return msg.id === editedMessage.id ? { ...msg, ...editedMessage } : msg;
                    });
                    return { ...current, results };
                });

                this.chatService.pinnedMessages.update((list: Array<IChatMessage>): Array<IChatMessage> => {
                    const current: Array<IChatMessage> = list ?? [];
                    return current.map((msg: IChatMessage): IChatMessage => {
                        return msg.id === editedMessage.id ? { ...msg, ...editedMessage } : msg;
                    });
                });
            });

        this.socketService.chatDeleteMessage
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((response: { message_ids: Array<number> }): void => {
                const messageIds: Array<number> = response?.message_ids ?? [];
                if (!messageIds.length) return;

                const deletedIds: Set<number> = new Set(messageIds);

                this.messages.update((current: IChatMessages): IChatMessages => {
                    const results: Array<IChatMessage> = current.results.map((msg: IChatMessage): IChatMessage => {
                        if (typeof msg.id !== 'number' || !deletedIds.has(msg.id)) return msg;

                        return {
                            ...msg,
                            deleted: true,
                            content: null,
                            attachments: []
                        };
                    });

                    return { ...current, results };
                });

                this.chatService.pinnedMessages.update((list: Array<IChatMessage>): Array<IChatMessage> => {
                    const currentPinned: Array<IChatMessage> = list ?? [];
                    const myUserId: number = this.sessionService.defaults.user_id;
                    const pendingRestoreIds: Set<number> = this.chatService.pendingRestoreMessageIds();

                    return currentPinned.filter((msg: IChatMessage): boolean => {
                        if (typeof msg.id !== 'number' || !deletedIds.has(msg.id)) return true;
                        const keepTemporarilyInPinned: boolean = msg.user?.id === myUserId && pendingRestoreIds.has(msg.id);
                        return keepTemporarilyInPinned;
                    });
                });

                this.chatService.reloadAttachments$.next();
            });

        this.socketService.chatNewPinnedMessage
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((response: { message: IChatMessage }): void => {
                const wsMessage: IChatMessage = response.message;

                this.messages.update((current: IChatMessages): IChatMessages => {
                    const results: Array<IChatMessage> = current.results.map((msg: IChatMessage) =>
                        msg.id === wsMessage.id ? { ...msg, is_pinned: true } : msg
                    );

                    const updated: IChatMessage | undefined = results.find(
                        (m: IChatMessage) => m.id === wsMessage.id
                    );

                    if (updated) {
                        this.chatService.pinnedMessages.update((list: Array<IChatMessage>) => {
                            const currentPinned: Array<IChatMessage> = list ?? [];
                            const without: Array<IChatMessage> = currentPinned.filter(
                                (m: IChatMessage) => m.id !== wsMessage.id
                            );
                            return [...without, updated];
                        });
                    }

                    return { ...current, results };
                });
            });

        this.socketService.chatRestoreMessage
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((response: { message: IChatMessage }): void => {
                const restoredMessage: IChatMessage = response.message;

                this.messages.update((current: IChatMessages): IChatMessages => {
                    const exists: boolean = current.results.some(
                        (msg: IChatMessage) => msg.id === restoredMessage.id
                    );

                    const results: Array<IChatMessage> = exists
                        ? current.results.map((msg: IChatMessage) =>
                            msg.id === restoredMessage.id ? restoredMessage : msg
                        )
                        : [...current.results, restoredMessage];

                    return { ...current, results };
                });

                this.chatService.pinnedMessages.update((list: Array<IChatMessage>): Array<IChatMessage> => {
                    const currentPinned: Array<IChatMessage> = list ?? [];
                    const withoutRestored: Array<IChatMessage> = currentPinned.filter((msg: IChatMessage) => {
                        return msg.id !== restoredMessage.id;
                    });

                    if (!restoredMessage.is_pinned) return withoutRestored;
                    return [...withoutRestored, restoredMessage];
                });
            });

        this.socketService.chatDeletePinnedMessage
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((response: { message_id: number }): void => {
                const id: number = response.message_id;

                this.chatService.pinnedMessages.update((list: Array<IChatMessage>) => {
                    const current: Array<IChatMessage> = list ?? [];
                    return current.filter((m: IChatMessage) => m.id !== id);
                });

                this.messages.update((current: IChatMessages): IChatMessages => {
                    const results: Array<IChatMessage> = current.results.map((msg: IChatMessage) =>
                        msg.id === id ? { ...msg, is_pinned: false } : msg
                    );
                    return { ...current, results };
                });
            });

        this.socketService.chatReadMessage
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((response: IChatReadMessage): void => {
                if (!response) return;
                this.messages.update((current: IChatMessages): IChatMessages => {
                    const results: Array<IChatMessage> = current.results.map((msg: IChatMessage) => {
                        if (typeof msg.id !== 'number' || !response.message_ids.includes(msg.id)) {
                            return msg;
                        }

                        const existingReadBy: Array<IChatReadUser> = msg.read_by ?? [];
                        const readUsersIds: Array<number> = existingReadBy.map((user: IChatReadUser) => user.id);

                        if (readUsersIds.includes(response.user.id)) {
                            return msg;
                        }

                        return {
                            ...msg,
                            read_by: [...existingReadBy, response.user]
                        };
                    });

                    return { ...current, results };
                });

                // Pinned tab renders from `chatService.pinnedMessages()`.
                // Keep `read_by` in sync so pinned status (read/sent) updates too.
                this.chatService.pinnedMessages.update((list: Array<IChatMessage>): Array<IChatMessage> => {
                    const currentPinned: Array<IChatMessage> = list ?? [];
                    return currentPinned.map((pinnedMsg: IChatMessage): IChatMessage => {
                        if (typeof pinnedMsg.id !== 'number') return pinnedMsg;
                        if (!response.message_ids.includes(pinnedMsg.id)) return pinnedMsg;

                        const existingReadBy: Array<IChatReadUser> = pinnedMsg.read_by ?? [];
                        const readUsersIds: Array<number> = existingReadBy.map((user: IChatReadUser) => user.id);
                        if (readUsersIds.includes(response.user.id)) return pinnedMsg;

                        return {
                            ...pinnedMsg,
                            read_by: [...existingReadBy, response.user]
                        };
                    });
                });
            });

        this.socketService.chatReactToMessage
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((response: IChatReactionSocketPayload): void => {
                this.applyReactionSocketUpdate(response, true);
            });

        this.socketService.chatDeleteReaction
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((response: IChatReactionSocketPayload): void => {
                this.applyReactionSocketUpdate(response, false);
            });

        this.chatService.loadNextMessages$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                const nextUrl: string = this.messages().next;
                if (!nextUrl || this.chatService.isLoadingNext()) return;

                this.chatService.loadNextMessages(nextUrl)
                    .pipe(takeUntilDestroyed(this.destroyRef))
                    .subscribe((response: IChatMessages) => {
                        this.messages.update((current: IChatMessages) => ({
                            ...response,
                            previous: current.previous,
                            results: [...response.results, ...current.results]
                        }));
                        this.readMessages();
                    });
            });

        this.chatService.loadPreviousMessages$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                const previousUrl: string = this.messages().previous;
                if (!previousUrl || this.chatService.isLoadingPrevious()) return;

                this.chatService.loadPreviousMessages(previousUrl)
                    .pipe(takeUntilDestroyed(this.destroyRef))
                    .subscribe((response: IChatMessages) => {
                        this.messages.update((current: IChatMessages) => ({
                            ...response,
                            next: current.next,
                            results: [...current.results, ...response.results]
                        }));
                        this.readMessages();
                    });
            });

        this.chatService.scrollToDay$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((date: string) => {
                if (this.chatService.isLoadingNext()) return;
                this.chatService.isLoadingNext.set(true);
                this.chatService
                    .getMessagesAround(this.chatService.id, 'date', { date: date, after_limit: 30, before_limit: 30, page_size: 30 })
                    .pipe(takeUntilDestroyed(this.destroyRef))
                    .subscribe({
                        next: (response: IChatMessages) => {
                            this.messages.set({
                                ...response,
                                anchor_message_id: undefined,
                                anchor_date: date
                            });
                            this.readMessages();
                            this.chatService.isLoadingNext.set(false);
                        },
                        error: (): void => {
                            this.chatService.isLoadingNext.set(false);
                            this.snackBarService.setErrorNotification();
                        }
                });
            });

        this.chatService.loadAroundMessage$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((messageId: number) => {
                const alreadyLoaded = this.messages().results.some((m: IChatMessage) => m.id === messageId);
                if (alreadyLoaded) {
                    this.chatService.scrollToMessage$.next(messageId);
                    return;
                }
                this.loadAroundMessage(messageId);
            });

    }

    private loadAroundMessage(messageId: number): void {
        this.chatService.isLoadingNext.set(true);
        this.chatService.highlightAnchor.set(true);
        this.chatService.getMessagesAround(
                this.chatService.id, 
                'message', 
                { after_limit: 30, before_limit: 30, page_size: 30, message_id: messageId }
            )
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (messages: IChatMessages): void => {
                    this.messages.set(messages);
                    this.loading.set(false);
                    this.readMessages();
                    this.chatService.isLoadingNext.set(false);
                },
                error: (): void => {
                    this.snackBarService.setErrorNotification();
                    this.loading.set(false);
                    this.chatService.isLoadingNext.set(false);
                }
            });
    }

    private loadLastMessages(): void {
        this.chatService.highlightAnchor.set(false);
        this.chatService.getMessages(this.chatService.id, this._requestParams.getParams())
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (messages: IChatMessages): void => {
                    this.messages.set(messages);    
                    this.loading.set(false);
                    this.chatService.markAsRead(this.chatService.id).subscribe();
                    setTimeout(() => this.chatService.scrollToBottom$.next());
                },
                error: (): void => {
                    this.snackBarService.setErrorNotification();
                    this.loading.set(false);
                }
            });
    }

    private loadLastReadMessages(): void {
        this.chatService.highlightAnchor.set(false);
        this.chatService.getMessagesAround(this.chatService.id, 'unread', { after_limit: 30, before_limit: 30, page_size: 30 })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (messages: IChatMessages): void => {
                    this.messages.set(messages);
                    this.loading.set(false);
                    this.readMessages();
                },
                error: (): void => {
                    this.snackBarService.setErrorNotification();
                    this.loading.set(false);
                }
            });
    }


    protected onTabChange(event: number): void {
        this.setActiveTab(event);
        const hasSearchQuery: boolean = this.chatService.searchPanelOpen()
            && this.chatService.searchPanelQuery().trim().length > 0;
        const mediaFilter: string = this.chatService.mediaSearchFilter().trim().toLowerCase();
        const documentsFilter: string = this.chatService.documentsSearchFilter().trim().toLowerCase();
        if (event === 2) {
            if (hasSearchQuery) return;
            const mediaLoadedWithCurrentFilter: boolean = this.lastLoadedAttachmentFilters.media === mediaFilter;
            if (this.loadingAttachmentsPage()) return;
            if (this.mediaAttachments() && mediaLoadedWithCurrentFilter) return;
            this.loadAttachments();
        }
        if (event === 3) {
            if (hasSearchQuery) return;
            const documentsLoadedWithCurrentFilter: boolean = this.lastLoadedAttachmentFilters.documents === documentsFilter;
            if (this.loadingAttachmentsPage()) return;
            if (this.documentsAttachments() && documentsLoadedWithCurrentFilter) return;
            this.loadAttachments();
        }
    }

    private setActiveTab(index: number): void {
        this.selectedTabIndex.set(index);
        this.chatService.activeChatTab.set(index);
    }

    protected onPinnedHeaderClick(): void {
        const pinned: Array<IChatMessage> = this.displayPinnedMessage();
        if (!pinned.length) return;

        const current: number = this.currentPinnedIndex();
        const safeCurrent: number = current < 0 ? pinned.length - 1 : Math.max(0, Math.min(current, pinned.length - 1));
        const message: IChatMessage | undefined = pinned[safeCurrent];
        const nextIndex: number = safeCurrent - 1 < 0 ? pinned.length - 1 : safeCurrent - 1;

        this.currentPinnedIndex.set(nextIndex);
        if (message && typeof message.id === 'number') {
            this.chatService.loadAroundMessage$.next(message.id);
        }
    }

    protected onGoToMainChat(messageId: number): void {
        this.setActiveTab(0);
        this.chatService.loadAroundMessage$.next(messageId);
    }

    protected onShowAttachmentInChat(messageId: number): void {
        this.setActiveTab(0);
        this.chatService.loadAroundMessage$.next(messageId);
    }

    private applyReactionSocketUpdate(response: IChatReactionSocketPayload, add: boolean): void {
        if (!response?.emoji || !response?.message_id || !response?.user_id) return;

        this.messages.update((current: IChatMessages): IChatMessages => {
            const results: Array<IChatMessage> = current.results.map((message: IChatMessage): IChatMessage => {
                if (message.id !== response.message_id) return message;

                const reactions: Array<{ emoji: string; users: Array<number> }> = [...(message.reactions ?? [])];
                const reactionIndex: number = reactions.findIndex((reaction: { emoji: string; users: Array<number> }): boolean => reaction.emoji === response.emoji);

                if (add && reactionIndex === -1) {
                    return {
                        ...message,
                        reactions: [...reactions, { emoji: response.emoji, users: [response.user_id] }]
                    };
                }

                if (!add && reactionIndex === -1) {
                    return message;
                }

                const currentReaction: { emoji: string; users: Array<number> } = reactions[reactionIndex];
                const hasUser: boolean = currentReaction.users.includes(response.user_id);
                const users: Array<number> = add
                    ? (hasUser ? currentReaction.users : [...currentReaction.users, response.user_id])
                    : currentReaction.users.filter((id: number): boolean => id !== response.user_id);

                if (!users.length) {
                    return {
                        ...message,
                        reactions: reactions.filter((reaction: { emoji: string; users: Array<number> }): boolean => reaction.emoji !== response.emoji)
                    };
                }

                reactions[reactionIndex] = { ...currentReaction, users };
                return {
                    ...message,
                    reactions
                };
            });

            return { ...current, results };
        });
    }

    private fileAttachmentToListItem(message: IChatMessage, a: IChatFileAttachment): IChatAttachment | null {
        if (typeof message.id !== 'number' || typeof a.id !== 'number') return null;
        const contentType: AllowedExtensions = String(a.content_type || '').toLowerCase() as AllowedExtensions;
        return {
            id: a.id,
            file: a.file,
            name: a.name,
            content_type: contentType,
            message: message.id,
            user: message.user,
            created_at: a.created_at ?? message.created_at,
            can_be_deleted: a.can_be_deleted ?? message.user.id === this.sessionService.defaults.user_id
        };
    }

    private isMediaListAttachment(attachment: IChatAttachment): boolean {
        const ext: string = String(attachment.content_type).toLowerCase();
        return (
            IMAGE_EXTENSIONS.includes(ext as AllowedExtensions) || VIDEO_EXTENSIONS.includes(ext as AllowedExtensions)
        );
    }

    private mergeNewAttachmentsFromMessage(savedMessage: IChatMessage): void {
        const attachments: Array<IChatFileAttachment> | undefined = savedMessage.attachments;
        if (!attachments?.length) return;

        const listItems: Array<IChatAttachment> = [];
        for (const a of attachments) {
            const item: IChatAttachment | null = this.fileAttachmentToListItem(savedMessage, a);
            if (item) listItems.push(item);
        }
        if (!listItems.length) {
            this.chatService.reloadAttachments$.next();
            return;
        }

        const mediaSearch: string = this.chatService.mediaSearchFilter().trim();
        const documentsSearch: string = this.chatService.documentsSearchFilter().trim();

        const mediaItems: Array<IChatAttachment> = listItems.filter((i: IChatAttachment): boolean =>
            this.isMediaListAttachment(i)
        );
        const docItems: Array<IChatAttachment> = listItems.filter(
            (i: IChatAttachment): boolean => !this.isMediaListAttachment(i)
        );

        const mediaNeedsReload: boolean = mediaItems.length > 0 && mediaSearch.length > 0;
        const documentsNeedReload: boolean = docItems.length > 0 && documentsSearch.length > 0;

        if (mediaNeedsReload || documentsNeedReload) {
            this.chatService.reloadAttachments$.next();
            return;
        }

        if (mediaItems.length) {
            this.mediaAttachments.update((current: IChatAttachments | null): IChatAttachments | null => {
                if (!current) return current;
                const existingIds: Set<number> = new Set<number>(current.results.map((r: IChatAttachment): number => r.id));
                const toAdd: Array<IChatAttachment> = mediaItems.filter((m: IChatAttachment): boolean => !existingIds.has(m.id));
                if (!toAdd.length) return current;
                return {
                    ...current,
                    results: [...toAdd, ...current.results],
                    count: current.count + toAdd.length
                };
            });
        }

        if (docItems.length) {
            this.documentsAttachments.update((current: IChatAttachments | null): IChatAttachments | null => {
                if (!current) return current;
                const existingIds: Set<number> = new Set<number>(current.results.map((r: IChatAttachment): number => r.id));
                const toAdd: Array<IChatAttachment> = docItems.filter((m: IChatAttachment): boolean => !existingIds.has(m.id));
                if (!toAdd.length) return current;
                return {
                    ...current,
                    results: [...toAdd, ...current.results],
                    count: current.count + toAdd.length
                };
            });
        }
    }

    private loadAttachments(): void {
        this.loadingAttachmentsPage.set(true);
        const seq: number = ++this.attachmentsRequestSeq;
        const activeTab: number = this.chatService.activeChatTab();
        const mediaSearch: string = this.chatService.mediaSearchFilter().trim().toLowerCase();
        const documentsSearch: string = this.chatService.documentsSearchFilter().trim().toLowerCase();

        const loadMedia = (): void => {
            this.chatService
                .getAttachments(this.chatService.id, {
                    page_size: 30,
                    category: 'image,video',
                    ...(mediaSearch ? { search: mediaSearch } : {})
                })
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe({
                    next: (attachments: IChatAttachments): void => {
                        if (seq !== this.attachmentsRequestSeq) return;
                        this.mediaAttachments.set(attachments);
                        this.lastLoadedAttachmentFilters.media = mediaSearch;
                    },
                    error: (): void => {
                        if (seq !== this.attachmentsRequestSeq) return;
                        this.snackBarService.setErrorNotification();
                        this.loadingAttachmentsPage.set(false);
                    },
                    complete: (): void => {
                        if (seq !== this.attachmentsRequestSeq) return;
                        this.loadingAttachmentsPage.set(false);
                    }
                });
        };

        const loadDocuments = (): void => {
            this.chatService
                .getAttachments(this.chatService.id, {
                    page_size: 30,
                    category: 'file',
                    ...(documentsSearch ? { search: documentsSearch } : {})
                })
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe({
                    next: (attachments: IChatAttachments): void => {
                        if (seq !== this.attachmentsRequestSeq) return;
                        this.documentsAttachments.set(attachments);
                        this.lastLoadedAttachmentFilters.documents = documentsSearch;
                    },
                    error: (): void => {
                        if (seq !== this.attachmentsRequestSeq) return;
                        this.snackBarService.setErrorNotification();
                        this.loadingAttachmentsPage.set(false);
                    },
                    complete: (): void => {
                        if (seq !== this.attachmentsRequestSeq) return;
                        this.loadingAttachmentsPage.set(false);
                    }
                });
        };

        if (activeTab === 2) {
            loadMedia();
            return;
        }

        if (activeTab === 3) {
            loadDocuments();
            return;
        }

        forkJoin([
            this.chatService.getAttachments(this.chatService.id, {
                page_size: 30,
                category: 'image,video',
                ...(mediaSearch ? { search: mediaSearch } : {})
            }),
            this.chatService.getAttachments(this.chatService.id, {
                page_size: 30,
                category: 'file',
                ...(documentsSearch ? { search: documentsSearch } : {})
            })
        ])
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (attachments: Array<IChatAttachments>): void => {
                    if (seq !== this.attachmentsRequestSeq) return;
                    this.mediaAttachments.set(attachments[0]);
                    this.documentsAttachments.set(attachments[1]);
                    this.lastLoadedAttachmentFilters.media = mediaSearch;
                    this.lastLoadedAttachmentFilters.documents = documentsSearch;
                },
                error: (): void => {
                    if (seq !== this.attachmentsRequestSeq) return;
                    this.snackBarService.setErrorNotification();
                },
                complete: (): void => {
                    if (seq !== this.attachmentsRequestSeq) return;
                    this.loadingAttachmentsPage.set(false);
                }
            });
    }

    protected loadMoreMedia(): void {
        const next = this.mediaAttachments()?.next;
        if (!next || this.loadingMoreMedia()) return;
        this.loadingMoreMedia.set(true);
        this.tableService
            .getNextTablePage<IChatAttachments, any>('GET', next)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (response: IChatAttachments): void => {
                    const current = this.mediaAttachments();
                    this.mediaAttachments.set({
                        ...response,
                        results: [...(current?.results ?? []), ...response.results]
                    });
                    this.loadingMoreMedia.set(false);
                },
                error: (): void => {
                    this.snackBarService.setErrorNotification();
                    this.loadingMoreMedia.set(false);
                }
            });
    }

    protected loadMoreDocuments(): void {
        const next = this.documentsAttachments()?.next;
        if (!next || this.loadingMoreDocuments()) return;
        this.loadingMoreDocuments.set(true);
        this.tableService
            .getNextTablePage<IChatAttachments, any>('GET', next)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (response: IChatAttachments): void => {
                    const current = this.documentsAttachments();
                    this.documentsAttachments.set({
                        ...response,
                        results: [...(current?.results ?? []), ...response.results]
                    });
                    this.loadingMoreDocuments.set(false);
                },
                error: (): void => {
                    this.snackBarService.setErrorNotification();
                    this.loadingMoreDocuments.set(false);
                }
            });
    }

    private readMessages(): void {
        const unreadedMessagesIds: Array<number | string> = this.messages().results
            .filter((msg: IChatMessage) => msg.user.id !== this.sessionService.defaults.user_id)
            .filter((msg: IChatMessage) => {
                const readUsersIds: Array<number> = msg.read_by?.map((user: { id: number }) => user.id) || [];
                return !readUsersIds.includes(this.sessionService.defaults.user_id);
            })
            .map((msg: IChatMessage) => msg.id);

        if (!unreadedMessagesIds.length) return;
        this.chatService.markAsRead(this.chatService.id, { ids: unreadedMessagesIds.join(',') })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe();
    }

    private snackHttpError(e: any): void {
        const body = e?.error;
        const messageText = body?.error || body;
        if (messageText) {
            this.snackBarService.setSnackBarNotification({ messageText: String(messageText) });
        } else {
            this.snackBarService.setErrorNotification();
        }
    }
}
